import {
  ChangeEvent,
  FocusEvent,
  forwardRef,
  KeyboardEvent,
  memo,
  useState,
} from 'react';
import { toNumeric } from '../number-input/number-input.utils';
import { clamp } from './stepper.utils';
import { StepperProps } from './stepper.types';
import { StepButton, StepperInput } from './stepper.styled';
import { Icons } from 'assets';
import { SVG } from '../../../svg';

export const Stepper = memo(
  forwardRef<HTMLInputElement, StepperProps>((props, ref) => {
    const {
      value,
      onChange,
      step = 1,
      min = 0,
      max = Number.MAX_SAFE_INTEGER,
      decimals = 0,
      size = 's',
      disabled,
      ...rest
    } = props;

    const [text, setText] = useState<string>(() => String(value));
    const [prevValue, setPrevValue] = useState<number>(value);

    // Правка состояния при смене пропа - штатный приём React, эффект тут не нужен
    if (!Object.is(value, prevValue)) {
      setPrevValue(value);
      setText(String(value));
    }

    const commit = (next: number) => {
      const clamped = clamp(next, min, max, decimals);

      setPrevValue(clamped);
      setText(String(clamped));
      onChange(clamped);
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;

      setText(raw);

      if (raw !== '') {
        const parsed = Number(raw);

        if (!Number.isNaN(parsed) && Number.isSafeInteger(Math.trunc(parsed))) {
          setPrevValue(parsed);
          onChange(parsed);
        }
      }
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      commit(Number(toNumeric(event.target.value, decimals)) || min);
      rest.onBlur?.(event);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      const isStep = event.key === 'ArrowUp' || event.key === 'ArrowDown';

      if (isStep && !rest.readOnly) {
        event.preventDefault();
        commit(value + (event.key === 'ArrowUp' ? step : -step));
      }

      rest.onKeyDown?.(event);
    };

    const minusDisabled = disabled || rest.readOnly || value <= min;
    const plusDisabled = disabled || rest.readOnly || value >= max;

    return (
      <StepperInput
        ref={ref}
        size={size}
        decimals={decimals}
        disabled={disabled}
        value={text}
        onChange={handleChange}
        {...rest}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        prefix={
          <StepButton
            iconButton={<SVG src={Icons.Minus} />}
            mode="neutral"
            variant="ghost"
            size="xs"
            disabled={minusDisabled}
            aria-label="Уменьшить"
            onClick={() => commit(value - step)}
          />
        }
        suffix={
          <StepButton
            iconButton={<SVG src={Icons.Plus} />}
            mode="neutral"
            variant="ghost"
            size="xs"
            disabled={plusDisabled}
            aria-label="Увеличить"
            onClick={() => commit(value + step)}
          />
        }
      />
    );
  })
);

Stepper.displayName = 'Stepper';
